import * as React from 'react';

import { TextInput } from '@acceligentllc/storybook';

import type { CellError } from 'af-components/RHFTableCellComponents/error.types';
import type { InputCellType } from 'af-components/RHFTableCellComponents/Input/types';

import type { InputType } from '../types';

type FieldValue = string | number | undefined;

/**
 * RHFFieldProps are props coming from field in react-hook-form
 */
interface RHFFieldProps {
	name: string;
	value: FieldValue;
	onChange: (value: string) => void;
	onBlur: (event) => void;
}

interface Props extends RHFFieldProps {
	elementKey: string;
	/**
	 * For now type of input cell can be: text, numeric, dollar or quantity cell
	 * Depending on type, design is decided
	*
	* @default text
	*/
	type?: InputType;
	placeholder?: string;
	className?: string;
	error?: CellError;
	label?: string;
	autoFocus?: boolean;
}

const isNumericInputType = (type: InputCellType) => {
	return type === 'numeric'
		|| type === 'dollar'
		|| type === 'quantity'
		|| type === 'percent';
};

const sanitizeNumericInputValue = (value: string) => {
	const normalizedValue = value.replace(',', '.');

	const numericCharactersOnly = normalizedValue.replace(/[^\d.]/g, '');

	const firstDecimalPointIndex = numericCharactersOnly.indexOf('.');

	if (firstDecimalPointIndex === -1) {
		return numericCharactersOnly;
	}

	const beforeDecimalPoint = numericCharactersOnly.slice(0, firstDecimalPointIndex + 1);
	const afterDecimalPoint = numericCharactersOnly
		.slice(firstDecimalPointIndex + 1)
		.replace(/\./g, '');

	return `${beforeDecimalPoint}${afterDecimalPoint}`;
};

export const Input: React.FC<Props> = React.memo((props) => {

	const {
		placeholder,
		type = 'text',
		name,
		value,
		onChange,
		onBlur,
		error,
		label,
		autoFocus = false,
	} = props;

	const handleChange = React.useCallback((newValue: string) => {
		if (!onChange) {
			return;
		}

		onChange(isNumericInputType(type) ? sanitizeNumericInputValue(newValue) : newValue);
	}, [onChange, type]);

	return (
		<TextInput
			autoFocus={autoFocus}
			error={!!error?.message ? error.message : undefined}
			hasError={!!error}
			label={label}
			name={name}
			onBlur={onBlur}
			onChange={handleChange}
			placeholder={placeholder}
			type="text"
			value={value === undefined ? '' : String(value)}
		/>
	);
});
